import {
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber as libIsValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import type { PhoneCountry } from "../types";

export const PHONE_COUNTRIES: readonly PhoneCountry[] =
  Object.freeze(getCountries());

// Sorted longest-first so +1868 (Trinidad) matches before +1 (US) etc.
const SORTED_DIAL_CODES: string[] = [...PHONE_COUNTRIES]
  .map((code) => `+${getCountryCallingCode(code)}`)
  .sort((a, b) => b.length - a.length);

// First country registered for each dial code (best-guess for ambiguous codes).
const DIAL_CODE_PRIMARY_COUNTRY = new Map<string, PhoneCountry>();
for (const code of PHONE_COUNTRIES) {
  const dc = `+${getCountryCallingCode(code)}`;
  if (!DIAL_CODE_PRIMARY_COUNTRY.has(dc)) {
    DIAL_CODE_PRIMARY_COUNTRY.set(dc, code);
  }
}

export function getDialCode(country: PhoneCountry): string {
  return `+${getCountryCallingCode(country)}`;
}

export function parseE164(value: string | undefined): {
  country: PhoneCountry | undefined;
  national: string;
} {
  if (!value) return { country: undefined, national: "" };

  const parsed = parsePhoneNumberFromString(value);
  if (parsed) {
    return {
      country: parsed.country,
      national: parsed.nationalNumber?.toString() ?? "",
    };
  }

  // Incomplete E.164: strip known dial code to preserve national digits while typing.
  for (const dialCode of SORTED_DIAL_CODES) {
    if (value.startsWith(dialCode)) {
      return {
        country: DIAL_CODE_PRIMARY_COUNTRY.get(dialCode),
        national: value.slice(dialCode.length),
      };
    }
  }

  return { country: undefined, national: digitsOnly(value) };
}

export function isValidPhoneNumber(
  phone: string,
  country: PhoneCountry,
): boolean {
  if (!phone) return false;
  const parsed = parsePhoneNumberFromString(phone);
  if (!parsed) return false;
  if (parsed.country !== country) return false;
  return libIsValidPhoneNumber(phone, country);
}

export function toLatinDigits(input: string): string {
  const arabicIndic = "٠١٢٣٤٥٦٧٨٩";
  const extendedArabicIndic = "۰۱۲۳۴۵۶۷۸۹";
  const latin = "0123456789";

  let result = input;
  for (let i = 0; i < arabicIndic.length; i++) {
    result = result.replaceAll(arabicIndic[i], latin[i]);
  }
  for (let i = 0; i < extendedArabicIndic.length; i++) {
    result = result.replaceAll(extendedArabicIndic[i], latin[i]);
  }
  return result;
}

export function digitsOnly(input: string): string {
  return toLatinDigits(input).replaceAll(/\D/g, "");
}
